import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [footerHero, setFooterHero] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  /* ===== FOOTER HERO SCROLL LOGIC ===== */
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;

      const nearBottom = scrollY + windowHeight > docHeight - 220;
      setFooterHero(nearBottom);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleFaqClick = () => {
    if (location.pathname !== "/") {
      navigate("/#faq");
    } else {
      const el = document.getElementById("faq");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      className={`
        relative z-10
        transition-all duration-700 ease-out
        bg-[#1f2d1f] text-[#f3f4ee]
        ${footerHero
          ? "rounded-t-[80px] pt-32 pb-20 -mt-32"
          : "rounded-t-[48px] pt-24 pb-12 -mt-20"}
      `}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-20">

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-12 md:gap-16 text-sm">

          {/* COLUMN 1: Brand / description */}
          <div className="md:col-span-1 space-y-6 text-center md:text-left">
            <div className="space-y-2">
              <h3 className="text-2xl font-serif font-bold tracking-tight text-white">
                EXPEDITIO
              </h3>
              <div className="w-12 h-[2px] bg-[#5b6f00] mx-auto md:mx-0" />
            </div>
            <p className="text-[#f3f4ee]/60 max-w-xs leading-relaxed text-base mx-auto md:mx-0">
              Experience the world, your way. Smarter, clearer, and more realistic travel planning.
            </p>
          </div>

          {/* COLUMN 2: Top Cities */}
          <div className="space-y-4 md:space-y-6 text-center md:text-left">
            <h4 className="font-bold uppercase tracking-wider text-xs text-[#5b6f00]">Top Cities</h4>
            <ul className="space-y-3 text-[#f3f4ee]/70">
              <li><Link to="/cities/goa" className="hover:text-white transition-colors">Goa</Link></li>
              <li><Link to="/cities/bali" className="hover:text-white transition-colors">Bali</Link></li>
              <li><Link to="/cities/dubai" className="hover:text-white transition-colors">Dubai</Link></li>
              <li><Link to="/cities/paris" className="hover:text-white transition-colors">Paris</Link></li>
              <li><Link to="/cities/tokyo" className="hover:text-white transition-colors">Tokyo</Link></li>
              <li><Link to="/cities/london" className="hover:text-white transition-colors">London</Link></li>
            </ul>
          </div>

          {/* COLUMN 3: Popular Itineraries */}
          <div className="space-y-4 md:space-y-6 text-center md:text-left">
            <h4 className="font-bold uppercase tracking-wider text-xs text-[#5b6f00]">Popular Itineraries</h4>
            <ul className="space-y-3 text-[#f3f4ee]/70">
              <li><Link to="/itineraries/goa" className="hover:text-white transition-colors">Goa itinerary</Link></li>
              <li><Link to="/itineraries/sikkim-gangtok" className="hover:text-white transition-colors">Sikkim</Link></li>
              <li><Link to="/itineraries/rajastan-royal" className="hover:text-white transition-colors">Rajasthan</Link></li>
              <li><Link to="/itineraries/kerala-backwater" className="hover:text-white transition-colors">Kerala</Link></li>
              <li><Link to="/itineraries/bali" className="hover:text-white transition-colors">Bali</Link></li>
              <li><Link to="/itineraries/paris" className="hover:text-white transition-colors">Paris</Link></li>
            </ul>
          </div>

          {/* COLUMN 4: Travel Guides / Resources */}
          <div className="space-y-4 md:space-y-6 text-center md:text-left">
            <h4 className="font-bold uppercase tracking-wider text-xs text-[#5b6f00]">Travel Guides</h4>
            <ul className="space-y-3 text-[#f3f4ee]/70">
              <li><Link to="/blogs/goa-travel-guide" className="hover:text-white transition-colors">Goa guide</Link></li>
              <li><Link to="/blogs/bali-travel-guide" className="hover:text-white transition-colors">Bali guide</Link></li>
              <li><Link to="/blogs/dubai-travel-guide" className="hover:text-white transition-colors">Dubai guide</Link></li>
              <li><Link to="/blogs/paris-travel-guide" className="hover:text-white transition-colors">Paris guide</Link></li>
              <li><Link to="/blogs" className="hover:text-white transition-colors">Blogs</Link></li>
              <li><Link to="/cities" className="hover:text-white transition-colors">Destinations</Link></li>
            </ul>
          </div>

          {/* COLUMN 5: Community / Support */}
          <div className="space-y-4 md:space-y-6 text-center md:text-left">
            <h4 className="font-bold uppercase tracking-wider text-xs text-[#5b6f00]">Support</h4>
            <ul className="space-y-3 text-[#f3f4ee]/70">
              <li>
                <button onClick={handleFaqClick} className="hover:text-white transition-colors text-left">
                  FAQ
                </button>
              </li>
              <li>
                <a href="https://instagram.com/expeditio_world/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Instagram
                </a>
              </li>
              <li>
                <Link to="/become-guide" className="hover:text-white transition-colors">
                  Become Guide
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-xs text-[#f3f4ee]/40">
          <div>© {currentYear} Expeditio Travel. All rights reserved.</div>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
